import { useEffect } from 'react';

import { useFormDataQuery } from '@/api/getters';
import { useFormMutation, useSubmitStepMutation } from '@/api/setters';
import { Stages, Steps } from '@/api/types';
import { formMutationDto } from '@/dto/formMutationDto';
import ConsultantCompletedStageI from '@/features/v2/hh/components/consultantCompleted/ConsultantCompletedStageI';
import ConsultantCompletedStageII from '@/features/v2/hh/components/consultantCompleted/ConsultantCompletedStageII';
import {
  INSURER_DECISION,
  INSURER_DECISION_NAME,
} from '@/features/v2/hh/components/RadioGroupIssuerDecision';
import {
  WILL_APPEAL,
  WILL_APPEAL_NAME,
} from '@/features/v2/hh/components/RadioGroupWillAppeal';
import { StepForm } from '@/features/v2/hh/StepForm';
import { HHSchema } from '@/features/v2/hh/stepsSchema';
import Documents from '@/features/v2/hh/surveys/documents/Documents';
import SurveyOne from '@/features/v2/hh/surveys/SurveyOne';
import SurveyTwo from '@/features/v2/hh/surveys/SurveyTwo';
import { useStepper } from '@/features/v2/lib/stepper';
import {
  getUserType,
  handleNextStageDataLayer,
} from '@/helpers/addStageToDataLyer';
import { scrollToTop } from '@/helpers/scrollToTop';

import { useAdditionalInfoContext } from '../lib/additionalInfo';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const Step1HH = () => {
  const { i18n } = useLingui();
  const { handleSubmit } = useFormContext();
  const { step, innerStep, nextInnerStep, isDeadEnd, innerObject } =
    useStepper();
  const { watch } = useFormContext();
  const { mutate } = useFormMutation();

  const { additionalInfo } = useAdditionalInfoContext();

  const lastStep = HHSchema[step]?.lastStep?.(watch(), additionalInfo);
  const { data } = useFormDataQuery();

  const userType = getUserType(data);

  const insurerDecision = watch(INSURER_DECISION_NAME);
  const willAppeal = watch(WILL_APPEAL_NAME);
  const isNegativeNoAppeal =
    insurerDecision === INSURER_DECISION.NEGATIVE &&
    willAppeal === WILL_APPEAL.NO;

  return (
    <>
      {data?.consultant_completed_at?.stage_I && step === 0 && (
        <ConsultantCompletedStageI />
      )}
      <StepForm
        backButton={!!HHSchema[step]?.backButton?.(innerStep, watch())}
        submitButton={{
          label: isNegativeNoAppeal
            ? i18n.t('Zakończ formularz')
            : i18n.t('Gotowe, przechodzę dalej'),
        }}
        isLastStep={innerStep >= lastStep.lastStep}
        onSubmit={handleSubmit(() => {
          if (innerStep >= lastStep.lastStep) {
            data?.stage &&
              handleNextStageDataLayer(data?.stage, Stages.STAGE_II, userType);
            return;
          }
          !isDeadEnd && nextInnerStep();
          mutate({
            ...formMutationDto({
              form_data: watch(),
              stepper: {
                step,
                innerStep: { ...innerObject, [step]: innerStep + 1 },
              },
            }),
          });
        })}
        newSubmitStage
      >
        {step <= 2 && <SurveyOne />}
      </StepForm>
    </>
  );
};

export const Step2HH = () => {
  const { i18n } = useLingui();
  const { handleSubmit } = useFormContext();
  const { step, innerStep } = useStepper();

  const { watch } = useFormContext();

  const { additionalInfo } = useAdditionalInfoContext();

  const lastStep = HHSchema[step]?.lastStep?.(watch(), additionalInfo);
  const { data } = useFormDataQuery();

  const userType = getUserType(data);

  return (
    <>
      {data?.consultant_completed_at?.stage_II && step === 1 && (
        <ConsultantCompletedStageII />
      )}
      <StepForm
        backButton={!!HHSchema[step]?.backButton?.(innerStep, watch())}
        submitButton={{
          label: i18n.t('Przejdź dalej'),
        }}
        onSubmit={handleSubmit(() => {
          if (innerStep >= lastStep.lastStep) {
            data?.stage &&
              handleNextStageDataLayer(data?.stage, Stages.STAGE_III, userType);
            return;
          }
        })}
        newSubmitStage
        isLastStep={innerStep === lastStep.lastStep}
      >
        {step >= 1 && <SurveyTwo />}
      </StepForm>
    </>
  );
};

export const Step3HH = () => {
  const { i18n } = useLingui();
  const { handleSubmit } = useFormContext();
  const { step, innerStep, setStepTag, nextInnerStep, innerObject, isDeadEnd } =
    useStepper();
  const { watch } = useFormContext();
  const { mutate } = useFormMutation();
  const { data } = useFormDataQuery();

  const { additionalInfo } = useAdditionalInfoContext();

  const submitStep = useSubmitStepMutation();

  const lastStep = HHSchema[step]?.lastStep?.(watch(), additionalInfo);

  const userType = getUserType(data);

  useEffect(() => {
    if (data?.step === Steps.Step_II_finish) {
      submitStep.mutateAsync(Steps.Step_III_start).then(() => {
        setStepTag(Steps.Step_III_start);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.step]);

  return (
    <StepForm
      backButton={!!HHSchema[step]?.backButton?.(innerStep, watch())}
      submitButton={{
        label: i18n.t('Gotowe, zakończ formularz'),
      }}
      isLastStep={innerStep === lastStep.lastStep}
      onSubmit={handleSubmit(() => {
        if (innerStep >= lastStep.lastStep) {
          data?.stage &&
            handleNextStageDataLayer(data?.stage, Stages.FINISHED, userType);
          submitStep.mutateAsync(Steps.Step_III_finish).then(() => {
            setStepTag(Steps.Step_III_finish);
          });
          return;
        }
        !isDeadEnd && nextInnerStep();
        scrollToTop();
        mutate({
          ...formMutationDto({
            form_data: watch(),
            stepper: {
              step,
              innerStep: { ...innerObject, [step]: innerStep + 1 },
            },
          }),
        });
      })}
    >
      {step >= 2 && <Documents />}
    </StepForm>
  );
};
